// Copyright (c) 2012-2021 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved. NOTICE: All information contained herein
// is, and remains the property of GRAB. The intellectual and technical concepts contained herein are confidential, proprietary
// and controlled by GRAB and may be covered by patents, patents in process, and are protected by trade secret or copyright law.
//
// You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate, adapt or change this material
// in any way unless prior written permission is obtained from GRAB. Access to the source code contained herein is hereby
// forbidden to anyone except current GRAB employees or contractors with binding Confidentiality and Non-disclosure agreements
// explicitly covering such access.
//
// The copyright notice above does not evidence any actual or intended publication or disclosure of this source code,
// which includes information that is confidential and/or proprietary, and is a trade secret, of GRAB.
//
// ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR THROUGH USE OF THIS SOURCE
// CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE LAWS AND
// INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY
// OR IMPLY ANY RIGHTS TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING
// THAT IT MAY DESCRIBE, IN WHOLE OR IN PART.

import React, { useEffect, useRef } from 'react'
import ReactMapboxGl, { MapContext } from 'react-mapbox-gl'
import { getMapStyle, transformRequest } from './helpers'
import './Map.less'
import GeoFenceControl from './GeoFenceControl'
import Roads from './Roads'
import PropTypes from 'prop-types'
import { connect, useDispatch, useSelector } from 'react-redux'
import Cameras from './Cameras'
import { getBoundingBoxParams } from '../../utils/bbox'
import 'mapbox-gl-draw-freehand-mode'
import { Traces, StatusTraces } from './Traces'
import { ZoomControl, ControlGroup, RecenterControl } from 'components/map-controller'
import '../map-controller/index.less'
import {imageSources} from './Cameras'

export const MapBox = ReactMapboxGl({
  accessToken: process.env.REACT_APP_MAPBOX_ACCESS_TOKEN,
  transformRequest,
  minZoom: 1,
  maxZoom: 20
})

class Map extends React.Component {
  map

  componentDidMount () {
    if (this.map) {
      window.addEventListener('resize', () => {
        this.map.resize()
      })
    }
  }

  handleViewPortChangeEnd (event) {
    const { mapAction, onViewportChanged } = this.props
    const zoom = [event.getZoom()]
    const boundingBox = getBoundingBoxParams(event)
    let center = event.getCenter()
    center = [center.lng, center.lat]
    mapAction.onViewportChange({ zoom, center, boundingBox })
    onViewportChanged && onViewportChanged({ zoom, center, boundingBox })
  }

  handleStylLoaded (event) {
    const { onStyleLoad, mapAction } = this.props
    this.map = event
    const boundingBox = getBoundingBoxParams(event)
    mapAction.setViewport({ boundingBox })
    if (typeof onStyleLoad === 'function') {
      onStyleLoad(event, boundingBox)
    }
  }

  handleCenterClick (map) {
    const { mapAction } = this.props
    map.setPitch(0)
    map.setBearing(0)
    mapAction.setDefaultCenter()
    mapAction.onViewportChange({ boundingBox: getBoundingBoxParams(map) })
  }

  componentDidUpdate (prevProps) {
    const { isDownloadShow, isNavVisible } = this.props
    if (prevProps.isDownloadShow !== isDownloadShow || prevProps.isNavVisible !== isNavVisible) {
      if (this.map) {
        setTimeout(() => {
          this.map.resize()
        }, 200)
      }
    }
  }

  render () {
    const {
      geoFence = {},
      selectedCameraID,
      onCameraClick,
      onCameraHover,
      containerStyle = {},
      center,
      onMapClick,
      zoom,
      isGeoFenceVisible,
      fitBounds,
      fitBoundsOptions,
      isCameraFinderExpanded,
      traces,
      statusTraces,
      onCameraContextMenu
    } = this.props
    const controlMarginRight = isCameraFinderExpanded ? 378 : 10

    return (
      <MapBox
        movingMethod='jumpTo'
        zoom={zoom}
        center={center}
        fitBounds={fitBounds}
        fitBoundsOptions={fitBoundsOptions}
        onZoomEnd={this.handleViewPortChangeEnd.bind(this)}
        onMoveEnd={this.handleViewPortChangeEnd.bind(this)}
        trackResize
        onStyleLoad={this.handleStylLoaded.bind(this)}
        style={getMapStyle('mono')}
        onClick={onMapClick}
        containerStyle={{ height: '100%', width: '100%', ...containerStyle }}
      >
        {!!isGeoFenceVisible && <GeoFenceControl {...geoFence} />}
        <Roads zoom={zoom} />
        <Traces traces={traces} map={this.map}/>
        <StatusTraces statusTraces={statusTraces} map={this.map}/>
        <Cameras
          selectedCameraID={selectedCameraID}
          onCameraHover={onCameraHover}
          onCameraClick={onCameraClick}
          onCameraContextMenu={onCameraContextMenu}
        />
        <MapContext.Consumer>
          {map => {
            return (
              <>
                <ControlGroup style={{ bottom: 60, top: 'auto', right: controlMarginRight }}>
                  <ZoomControl map={map} />
                </ControlGroup>
                <ControlGroup style={{ bottom: 20, top: 'auto', right: controlMarginRight }}>
                  <RecenterControl onClick={this.handleCenterClick.bind(this, map)} />
                </ControlGroup>
              </>
            )
          }}
        </MapContext.Consumer>
      </MapBox>
    )
  }
}

Map.propTypes = {
  geoFence: PropTypes.shape(GeoFenceControl.propTypes)
}

const mapStateToProps = ({ map, download, app, cameraFinder, gpsTrace, city }) => ({
      ...map,
      isDownloadShow: download.isDownloadShow,
      isNavVisible: !app.menuCollapsed,
      isCameraFinderExpanded: cameraFinder.expanded,
      traces: gpsTrace.currentTrace,
      currentCity: city.current
    })
const mapDispatchToProps = ({ map }) => ({
    mapAction: map
  })
export default connect(mapStateToProps,mapDispatchToProps)(Map)

export const GeneralMap = ({containerStyle={}, onMapClick, onViewportChanged, children, onStyleLoad}) => {
  const {zoom, center, fitBounds, fitBoundsOptions,traces, currentCity, expanded} = useSelector(({map,gpsTrace, city, cameraFinder}) =>({
    ...map,
    traces: gpsTrace.currentTrace,
    currentCity: city.current,
    expanded: cameraFinder.expanded
  }))
  const {map: mapAction} = useDispatch()
  const self = useRef(null)
  useEffect(()=>{
      window.addEventListener('resize', handleMapResize)
      return ()=>{
        window.removeEventListener('resize', handleMapResize)
      }
  },[])

  useEffect(() => {
      handleMapResize()
  },[expanded])
  const handleMapResize = ()=>{
    const map = self.current
    if(!!map){
      map.resize()
    }
  }
  const handleViewPortChangeEnd = (event) => {
    const zoom = [event.getZoom()]
    const boundingBox = getBoundingBoxParams(event)
    let center = event.getCenter()
    center = [center.lng, center.lat]
    mapAction.onViewportChange({ zoom, center, boundingBox })
    onViewportChanged && onViewportChanged({ zoom, center, boundingBox })
  }
  const handleStylLoaded = (map) => {
    self.current = map
    const boundingBox = getBoundingBoxParams(map)
    mapAction.setViewport({ boundingBox })
    if (typeof onStyleLoad === 'function') {
      onStyleLoad(map, {boundingBox})
    }
    imageSources.forEach((imageSource)=>{
      map.loadImage(imageSource.url,(err,image)=>{
        map.addImage(imageSource.id,image)
      })
    })
  }
  const handleCenterClick = (map) => {
    map.setPitch(0)
    map.setBearing(0)
    mapAction.setDefaultCenter()
    mapAction.onViewportChange({ boundingBox: getBoundingBoxParams(map) })
  }
  return (
    <MapBox
      movingMethod='jumpTo'
      zoom={zoom}
      center={center}
      fitBounds={fitBounds}
      fitBoundsOptions={fitBoundsOptions}
      onZoomEnd={handleViewPortChangeEnd}
      onMoveEnd={handleViewPortChangeEnd}
      trackResize
      onStyleLoad={handleStylLoaded}
      style={getMapStyle('mono')}
      onClick={onMapClick}
      containerStyle={{ height: '100%', width: '100%', ...containerStyle }}
      // ref={self}
    >
      {children}
      <MapContext.Consumer>
        {map => {
          return (
            <>
              <ControlGroup style={{ bottom: 60, top: 'auto', right: 10 }}>
                <ZoomControl map={map} />
              </ControlGroup>
              <ControlGroup style={{ bottom: 20, top: 'auto', right: 10}}>
                <RecenterControl onClick={handleCenterClick.bind(null, map)} />
              </ControlGroup>
            </>
          )
        }}
      </MapContext.Consumer>
    </MapBox>
  )
}
